import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Minimize2, ExternalLink } from 'lucide-react';
import { HOME } from '@/constants/testIds';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

const WHATSAPP_NUMBER = '917795287075';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;

const WELCOME = {
  id: 'welcome',
  role: 'bot',
  text: "👋 Hi! I'm MGS AI Assistant. I can help you with questions about our services, portfolio, industries, and more. How can I help you today?",
  time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
};

const SUGGESTIONS = [
  'What services does MGS offer?',
  'How can I contact the team?',
  'Tell me about your portfolio',
  'What industries do you serve?',
  'Do you offer AI chatbot development?',
  'What technologies do you use?',
  'How much do your services cost?',
  'Where is MGS located?',
];

const HUMAN_KEYWORDS = [
  'human', 'agent', 'real person', 'talk to', 'connect with',
  'representative', 'support', 'customer service', 'speak to',
  'talk to someone', 'contact person', 'live agent',
];

function ChatBubble({ msg }) {
  const isBot = msg.role === 'bot';
  const showWhatsApp = msg.redirectWhatsApp || msg.suggestWhatsApp;
  return (
    <div className={`flex items-end gap-2 ${isBot ? 'justify-start' : 'justify-end'}`}>
      {isBot && (
        <div className="h-7 w-7 rounded-full overflow-hidden flex-shrink-0 mb-1 border border-slate-200 shadow-sm bg-white">
          <img src="/mgs-logo.png" alt="MGS" className="h-full w-full object-cover" />
        </div>
      )}
      <div className={`max-w-[78%] ${isBot ? 'order-2' : 'order-1'}`}>
        <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ${isBot ? 'bg-white text-gray-800 rounded-tl-sm' : 'bg-navy text-white rounded-tr-sm'}`}>
          {msg.text}
        </div>
        {showWhatsApp && (
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold bg-green-500 hover:bg-green-600 text-white rounded-full px-4 py-2 transition-colors shadow-sm"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
            Chat on WhatsApp
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <p className={`text-[10px] text-gray-400 mt-1 ${isBot ? 'text-left pl-1' : 'text-right pr-1'}`}>
          {msg.time}
        </p>
      </div>
    </div>
  );
}

function hasHumanKeywords(input) {
  const lower = input.toLowerCase();
  return HUMAN_KEYWORDS.some(k => lower.includes(k));
}

export default function AskVicky() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [usedSuggestions, setUsedSuggestions] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, typing]);

  function addMsg(msg) {
    setMessages(prev => [...prev, msg]);
  }

  function now() {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  async function handleSend(text) {
    const msg = text || input.trim();
    if (!msg) return;
    setInput('');

    if (SUGGESTIONS.includes(msg)) {
      setUsedSuggestions(prev => [...prev, msg]);
    }

    addMsg({ id: Date.now(), role: 'user', text: msg, time: now() });

    if (hasHumanKeywords(msg)) {
      addMsg({
        id: Date.now() + 1,
        role: 'bot',
        text: 'Sure! Click the WhatsApp button below to chat with our team directly.',
        time: now(),
        redirectWhatsApp: true,
      });
      return;
    }

    setTyping(true);

    try {
      const res = await fetch(`${API_BASE}/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });

      if (!res.ok) throw new Error('API error');

      const json = await res.json();
      setTyping(false);

      if (json.success && json.data) {
        addMsg({
          id: Date.now() + 1,
          role: 'bot',
          text: json.data.reply,
          time: now(),
          suggestWhatsApp: json.data.suggestWhatsApp,
          redirectWhatsApp: json.data.redirectWhatsApp,
        });
      } else {
        throw new Error('Invalid response');
      }
    } catch {
      setTyping(false);
      addMsg({
        id: Date.now() + 1,
        role: 'bot',
        text: "I'm having trouble right now. Would you like to connect with our team on WhatsApp?",
        time: now(),
        suggestWhatsApp: true,
      });
    }
  }

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-window"
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed bottom-24 right-6 z-50 w-[320px] rounded-2xl overflow-hidden shadow-2xl"
            style={{ boxShadow: '0 24px 60px rgba(31,45,69,0.25)' }}
          >
            <div className="bg-navy px-4 py-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full overflow-hidden flex-shrink-0 shadow border-2 border-white/30 bg-white">
                <img src="/mgs-logo.png" alt="MGS" className="h-full w-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-sm leading-tight">Ask MGS</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse"></span>
                  <span className="text-xs text-blue-200">Online · AI Assistant</span>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <Minimize2 className="h-4 w-4 text-white" />
              </button>
            </div>

            <div className="bg-navy/90 px-4 py-1.5 border-t border-white/10 flex items-center gap-2">
              <span className="text-[10px] text-blue-200/70">Powered by</span>
              <span className="text-[10px] font-semibold text-gold">Max Global Software</span>
              <span className="ml-auto text-[10px] text-blue-200/50">EST. 2005</span>
            </div>

            <div className="bg-slate-50 h-64 overflow-y-auto px-4 py-4 flex flex-col gap-3">
              {messages.map(msg => (
                <ChatBubble key={msg.id} msg={msg} />
              ))}

              {typing && (
                <div className="flex items-end gap-2 justify-start">
                  <div className="h-7 w-7 rounded-full overflow-hidden flex-shrink-0 border border-slate-200 shadow-sm bg-white">
                    <img src="/mgs-logo.png" alt="MGS" className="h-full w-full object-cover" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                    <div className="flex gap-1 items-center">
                      <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="bg-slate-50 border-t border-slate-100 px-3 py-1.5 overflow-x-auto no-scrollbar flex gap-1.5 flex-nowrap">
              {SUGGESTIONS.filter(s => !usedSuggestions.includes(s)).map(s => (
                <button
                  key={s}
                  onClick={() => handleSend(s)}
                  className="text-[11px] whitespace-nowrap bg-white border border-navy/20 text-navy rounded-full px-2.5 py-1 hover:bg-navy hover:text-white transition-colors flex-shrink-0"
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="bg-white border-t border-slate-100 px-3 py-3 flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                placeholder="Type your message..."
                className="flex-1 text-sm bg-slate-50 border border-slate-200 rounded-full px-4 py-2 outline-none focus:border-navy/40 focus:ring-2 focus:ring-navy/10 transition-all placeholder:text-gray-400"
              />

              <button
                onClick={() => handleSend()}
                disabled={!input.trim()}
                className="h-9 w-9 rounded-full bg-navy flex items-center justify-center flex-shrink-0 hover:bg-navy/80 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow"
              >
                <Send className="h-4 w-4 text-white" />
              </button>
            </div>

          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid={HOME.askVicky}
        onClick={() => setOpen(prev => !prev)}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-navy text-white pl-3 pr-4 py-2 shadow-soft hover:shadow-[0_14px_36px_rgba(31,45,69,0.35)] transition-shadow"
      >
        <span className="h-8 w-8 rounded-full overflow-hidden inline-flex items-center justify-center bg-white border border-white/30 shadow">
          {open
            ? <X className="h-4 w-4 text-navy" />
            : <img src="/mgs-logo.png" alt="MGS" className="h-full w-full object-cover" />}
        </span>
        <span className="text-sm font-semibold">Ask MGS</span>
        {!open && (
          <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-400 rounded-full border-2 border-white" />
        )}
      </motion.button>
    </>
  );
}
